import React, { useEffect, useMemo, useState } from 'react';
import {
  createAlert,
  deleteAlert,
  getAlertLogs,
  getAlerts,
  getDevices,
  getWebSocketUrl,
  sendTestEmail,
  sendTestTelegram,
  toggleAlert,
  updateAlert,
} from '../services/api';
import { Alert, AlertLog, Device } from '../types';
import { useTimezone } from '../context/TimezoneContext';

type AlertFormState = {
  device_id: string;
  name: string;
  metric: string;
  operator: string;
  threshold: string;
  cooldown_minutes: string;
  notify_email: string;
  notify_telegram: string;
};

type AlertTone = 'blue' | 'green' | 'amber' | 'violet' | 'danger' | 'neutral';
type AlertIconName =
  | 'bell'
  | 'device'
  | 'gauge'
  | 'thermometer'
  | 'condition'
  | 'mail'
  | 'telegram'
  | 'plus'
  | 'refresh'
  | 'edit'
  | 'pause'
  | 'play'
  | 'trash'
  | 'shield'
  | 'timeline'
  | 'droplet'
  | 'x';

const emptyForm: AlertFormState = {
  device_id: '',
  name: '',
  metric: 'temperature',
  operator: '>',
  threshold: '',
  cooldown_minutes: '15',
  notify_email: '',
  notify_telegram: '',
};

function normalizeEditableNumber(value: string, fallback: number) {
  if (value.trim() === '') return fallback;
  const next = Number(value);
  return Number.isFinite(next) ? next : fallback;
}

export default function AlertsPage() {
  const { formatTime } = useTimezone();
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [logs, setLogs] = useState<AlertLog[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [showFullHistory, setShowFullHistory] = useState(false);
  const [loading, setLoading] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testTelegram, setTestTelegram] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [selectedRuleId, setSelectedRuleId] = useState<string | null>(null);
  const [form, setForm] = useState<AlertFormState>(emptyForm);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const wsUrl = getWebSocketUrl('/ws');
    const ws = new WebSocket(wsUrl);

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.type === 'alert') {
          loadData();
        }
      } catch {
        // Ignore malformed WebSocket payloads.
      }
    };

    return () => ws.close();
  }, []);

  async function loadData() {
    try {
      const [rules, history, fleet] = await Promise.all([getAlerts(), getAlertLogs(), getDevices()]);
      setAlerts(rules);
      setLogs(history);
      setDevices(fleet);
      setSelectedRuleId((current) =>
        current && rules.some((rule: Alert) => rule.id === current) ? current : rules[0]?.id || null
      );
    } catch {
      // Keep the previous alert console visible.
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      const isStringMetric = getMetricType(form.device_id, form.metric) === 'string';
      const payload = {
        ...form,
        threshold: isStringMetric ? form.threshold : parseFloat(form.threshold),
        cooldown_minutes: normalizeEditableNumber(form.cooldown_minutes, 15),
      };
      if (editingId) {
        await updateAlert(editingId, payload);
      } else {
        await createAlert(payload);
      }
      closeForm();
      await loadData();
    } catch {
      window.alert(editingId ? 'Failed to update alert' : 'Failed to create alert');
    } finally {
      setLoading(false);
    }
  }

  function handleEdit(alert: Alert) {
    setSelectedRuleId(alert.id);
    setEditingId(alert.id);
    setForm({
      device_id: alert.device_id,
      name: alert.name,
      metric: alert.metric,
      operator: alert.operator,
      threshold: alert.threshold !== undefined && alert.threshold !== null ? String(alert.threshold) : '',
      cooldown_minutes: String(alert.cooldown_minutes ?? 15),
      notify_email: alert.notify_email || '',
      notify_telegram: alert.notify_telegram || '',
    });
    setShowForm(true);
  }

  function startCreate() {
    setEditingId(null);
    setForm(emptyForm);
    setShowForm(true);
  }

  function closeForm() {
    setShowForm(false);
    setEditingId(null);
    setForm(emptyForm);
  }

  async function handleDelete(id: string) {
    if (!window.confirm('Delete this alert?')) return;
    try {
      await deleteAlert(id);
      await loadData();
    } catch {
      window.alert('Failed to delete alert');
    }
  }

  async function handleToggle(alert: Alert) {
    try {
      await toggleAlert(alert.id, !alert.enabled);
      await loadData();
    } catch {
      window.alert('Failed to toggle alert');
    }
  }

  async function testEmailChannel() {
    if (!testEmail) return;
    try {
      await sendTestEmail(testEmail);
      window.alert('Test email sent successfully. Please check the inbox for ' + testEmail + '.');
      setTestEmail('');
    } catch {
      window.alert('Failed to send test email. Check SMTP configuration.');
    }
  }

  async function testTelegramChannel() {
    if (!testTelegram) return;
    try {
      await sendTestTelegram(testTelegram);
      window.alert('Test Telegram sent. Check your Telegram.');
      setTestTelegram('');
    } catch {
      window.alert('Failed to send Telegram. Check bot token configuration.');
    }
  }

  function getMetricType(deviceId: string, metric: string): 'number' | 'string' | null {
    const device = devices.find((d) => d.id === deviceId);
    if (!device?.parser_config?.mappings?.[metric]) return null;
    return device.parser_config.mappings[metric].type || 'number';
  }

  function getMetricOptions(deviceId: string): Array<{ value: string; label: string; type: 'number' | 'string' }> {
    const device = devices.find((d) => d.id === deviceId);
    if (!device) return [];

    const options: Array<{ value: string; label: string; type: 'number' | 'string' }> = [];

    if (!device.mqtt_connection_id && !device.gateway_topic) {
      options.push(
        { value: 'temperature', label: 'Temperature', type: 'number' },
        { value: 'humidity', label: 'Humidity', type: 'number' },
        { value: 'pressure', label: 'Pressure', type: 'number' },
        { value: 'battery', label: 'Battery', type: 'number' },
        { value: 'signal_strength', label: 'Signal strength', type: 'number' }
      );
    }

    if (device.parser_config?.mappings) {
      for (const [key, mapping] of Object.entries(device.parser_config.mappings as Record<string, any>)) {
        options.push({
          value: key,
          label: mapping.label || key.replace(/_/g, ' '),
          type: mapping.type || 'number',
        });
      }
    }

    return options;
  }

  const deviceById = useMemo(
    () => new Map(devices.map((device) => [device.id, device])),
    [devices]
  );
  const selectedRule = selectedRuleId
    ? alerts.find((alert) => alert.id === selectedRuleId) || null
    : alerts[0] || null;
  const selectedDevice = selectedRule ? deviceById.get(selectedRule.device_id) : undefined;
  const selectedLatestLog = selectedRule ? logs.find((log) => log.alert_id === selectedRule.id) : null;
  const activeRules = alerts.filter((alert) => alert.enabled);
  const pausedRules = alerts.length - activeRules.length;
  const recentWindowCount = logs.filter((log) => Date.now() - new Date(log.triggered_at).getTime() < 24 * 60 * 60 * 1000).length;
  const notifiedRules = alerts.filter((alert) => alert.notify_email || alert.notify_telegram).length;
  const channelCoverage = alerts.length > 0 ? Math.round((notifiedRules / alerts.length) * 100) : 0;
  const workflowSteps = getWorkflowSteps(selectedRule, selectedDevice, selectedLatestLog);

  return (
    <div className="sd-alerts-page">
      <section className="sd-page-header sd-alert-header">
        <div className="sd-heading sd-alert-heading">
          <div className="sd-alert-title-row">
            <span className="sd-alert-title-icon" aria-hidden="true">
              <AlertIcon icon="bell" />
            </span>
            <div>
              <div className="sd-eyebrow">ALERTS</div>
              <h1>Alerts</h1>
              <p>Guided alert builder for devices, thresholds, and notification confidence.</p>
            </div>
          </div>
        </div>
        <div className="sd-page-actions">
          <button className="sd-secondary-button sd-alert-action-button" onClick={() => loadData()} type="button">
            <span className="sd-alert-button-icon" aria-hidden="true">
              <AlertIcon icon="refresh" />
            </span>
            Refresh
          </button>
          <button
            className="sd-primary-button sd-alert-action-button"
            onClick={() => (showForm ? closeForm() : startCreate())}
            type="button"
          >
            <span className="sd-alert-button-icon" aria-hidden="true">
              <AlertIcon icon={showForm ? 'x' : 'plus'} />
            </span>
            {showForm ? 'Close Builder' : 'New Rule'}
          </button>
        </div>
      </section>

      <section className="sd-alert-builder-intro" aria-label="Guided alert setup">
        <div className="sd-alert-flow-grid">
          {workflowSteps.map((step) => (
            <article className={`sd-alert-flow-card is-${step.tone}`} key={step.title}>
              <div className="sd-alert-flow-top">
                <span className="sd-alert-step-number">{step.number}</span>
                <span className="sd-alert-flow-icon" aria-hidden="true">
                  <AlertIcon icon={step.icon} />
                </span>
              </div>
              <strong>{step.title}</strong>
              <p>{step.primary}</p>
              <span className="sd-alert-step-state">
                <i />
                {step.secondary}
              </span>
            </article>
          ))}
        </div>

        <aside className="sd-alert-confidence-strip" aria-label="Alert confidence summary">
          <AlertStat label="Rules" value={alerts.length} detail={`${activeRules.length} active`} tone="blue" />
          <AlertStat label="Triggers" value={recentWindowCount} detail="Last 24h" tone="danger" />
          <AlertStat label="Coverage" value={`${channelCoverage}%`} detail={`${notifiedRules}/${alerts.length || 0} notified`} tone="green" />
          <AlertStat label="Paused" value={pausedRules} detail={pausedRules === 1 ? 'Rule' : 'Rules'} tone="violet" />
        </aside>
      </section>

      {showForm && (
        <AlertRuleForm
          devices={devices}
          editingId={editingId}
          form={form}
          getMetricOptions={getMetricOptions}
          getMetricType={getMetricType}
          handleSubmit={handleSubmit}
          loading={loading}
          onCancel={closeForm}
          setForm={setForm}
        />
      )}

      <section className="sd-alert-builder-workspace">
        <article className="sd-panel sd-alert-coverage-panel">
          <div className="sd-panel-header sd-alert-panel-header">
            <div>
              <h2>Alert Coverage</h2>
              <p>{alerts.length} rule{alerts.length === 1 ? '' : 's'} across this workspace.</p>
            </div>
            <button className="sd-alert-icon-command" onClick={startCreate} type="button" aria-label="Create alert rule" title="Create alert rule">
              <AlertIcon icon="plus" />
            </button>
          </div>

          <div className="sd-alert-rule-list">
            {alerts.map((alert) => (
              <RuleCard
                alert={alert}
                device={deviceById.get(alert.device_id)}
                handleDelete={handleDelete}
                handleEdit={handleEdit}
                handleToggle={handleToggle}
                key={alert.id}
                selected={selectedRule?.id === alert.id}
                setSelectedRuleId={setSelectedRuleId}
              />
            ))}
            {alerts.length === 0 && <AlertEmptyState label="No alert rules configured yet." />}
          </div>
        </article>

        <article className="sd-panel sd-alert-selected-panel">
          <RuleInspector
            device={selectedDevice}
            formatTime={formatTime}
            onDelete={handleDelete}
            onEdit={handleEdit}
            onToggle={handleToggle}
            rule={selectedRule}
          />

          <div className="sd-alert-selected-bottom">
            <AlertTimeline
              expanded={showFullHistory}
              formatTime={formatTime}
              logs={logs}
              onToggleExpanded={() => setShowFullHistory((current) => !current)}
            />
            <NotificationChannels
              testEmail={testEmail}
              testEmailChannel={testEmailChannel}
              testTelegram={testTelegram}
              testTelegramChannel={testTelegramChannel}
              setTestEmail={setTestEmail}
              setTestTelegram={setTestTelegram}
            />
          </div>
        </article>
      </section>
    </div>
  );
}

function AlertRuleForm({
  devices,
  editingId,
  form,
  getMetricOptions,
  getMetricType,
  handleSubmit,
  loading,
  onCancel,
  setForm,
}: {
  devices: Device[];
  editingId: string | null;
  form: AlertFormState;
  getMetricOptions: (deviceId: string) => Array<{ value: string; label: string; type: 'number' | 'string' }>;
  getMetricType: (deviceId: string, metric: string) => 'number' | 'string' | null;
  handleSubmit: (event: React.FormEvent) => void;
  loading: boolean;
  onCancel: () => void;
  setForm: React.Dispatch<React.SetStateAction<AlertFormState>>;
}) {
  const metricType = getMetricType(form.device_id, form.metric);

  return (
    <form className="sd-alert-guided-form" onSubmit={handleSubmit}>
      <div className="sd-alert-guided-head">
        <div className="sd-alert-form-title">
          <span className="sd-alert-form-icon" aria-hidden="true">
            <AlertIcon icon="bell" />
          </span>
          <div>
            <h2>{editingId ? 'Edit Alert Builder' : 'Guided Alert Builder'}</h2>
            <p>{editingId ? 'Tune this rule without losing its delivery setup.' : 'Create a rule in four simple steps.'}</p>
          </div>
        </div>
        <button className="sd-alert-icon-command" onClick={onCancel} type="button" aria-label="Close alert builder" title="Close alert builder">
          <AlertIcon icon="x" />
        </button>
      </div>

      <div className="sd-alert-form-steps">
        <section className="sd-alert-form-step is-blue">
          <StepHeading icon="device" number="1" title="Choose Device" copy="Name the rule and pick the asset operators should watch." />
          <div className="sd-alert-form-field-grid">
            <Field label="Rule name" hint="Short name operators will recognize in notifications.">
              <input
                placeholder="e.g. High temperature"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </Field>
            <Field label="Device" hint="Device whose mapped telemetry should be evaluated.">
              <select
                required
                value={form.device_id}
                onChange={(e) => setForm({ ...form, device_id: e.target.value, metric: '', operator: '>', threshold: '' })}
              >
                <option value="">Select device...</option>
                {devices.map((device) => (
                  <option key={device.id} value={device.id}>{device.name}</option>
                ))}
              </select>
            </Field>
          </div>
        </section>

        <section className="sd-alert-form-step is-green">
          <StepHeading icon={getMetricIcon(form.metric)} number="2" title="Pick Metric" copy="Use a mapped telemetry field or a standard direct-sensor metric." />
          <Field label="Metric" hint="Metric key from the selected device's field mappings or latest telemetry.">
            <select
              disabled={!form.device_id}
              value={form.metric}
              onChange={(e) => {
                const metric = e.target.value;
                const nextType = getMetricType(form.device_id, metric);
                setForm({ ...form, metric, operator: nextType === 'string' ? '=' : '>', threshold: '' });
              }}
            >
              <option value="">Select metric...</option>
              {getMetricOptions(form.device_id).map((metric) => (
                <option key={metric.value} value={metric.value}>{metric.label}</option>
              ))}
            </select>
          </Field>
        </section>

        <section className="sd-alert-form-step is-amber">
          <StepHeading icon="condition" number="3" title="Set Condition" copy="Define the threshold and cooldown before the next notification." />
          <div className="sd-alert-form-field-grid is-condition">
            <Field label="Operator" hint="Comparison used against incoming values.">
              <select value={form.operator} onChange={(e) => setForm({ ...form, operator: e.target.value })}>
                {metricType === 'string' ? (
                  <>
                    <option value="=">=</option>
                    <option value="!=">!=</option>
                  </>
                ) : (
                  <>
                    <option value=">">&gt;</option>
                    <option value="<">&lt;</option>
                    <option value=">=">&gt;=</option>
                    <option value="<=">&lt;=</option>
                    <option value="=">=</option>
                    <option value="!=">!=</option>
                  </>
                )}
              </select>
            </Field>
            <Field label="Threshold" hint="Numeric limit or text state that triggers this rule.">
              <input
                placeholder={metricType === 'string' ? 'e.g. ON' : 'e.g. 85.0'}
                required
                step="0.1"
                type={metricType === 'string' ? 'text' : 'number'}
                value={form.threshold}
                onChange={(e) => setForm({ ...form, threshold: e.target.value })}
              />
            </Field>
            <Field label="Cooldown" hint="Blank saves as 15 minutes.">
              <input
                placeholder="15"
                type="number"
                value={form.cooldown_minutes}
                onChange={(e) => setForm({ ...form, cooldown_minutes: e.target.value })}
              />
            </Field>
          </div>
        </section>

        <section className="sd-alert-form-step is-violet">
          <StepHeading icon="mail" number="4" title="Notify Team" copy="Send alerts to email, Telegram, or both." />
          <div className="sd-alert-form-field-grid">
            <Field label="Notify email" hint="Leave blank to skip email notifications.">
              <input
                placeholder="engineer@company.com"
                type="email"
                value={form.notify_email}
                onChange={(e) => setForm({ ...form, notify_email: e.target.value })}
              />
            </Field>
            <Field label="Telegram chat ID" hint="Leave blank to skip Telegram.">
              <input
                placeholder="123456789"
                value={form.notify_telegram}
                onChange={(e) => setForm({ ...form, notify_telegram: e.target.value })}
              />
            </Field>
          </div>
        </section>
      </div>

      <div className="sd-alert-form-actions">
        <button className="sd-secondary-button" onClick={onCancel} type="button">
          Cancel
        </button>
        <button className="sd-primary-button sd-alert-action-button" disabled={loading} type="submit">
          <span className="sd-alert-button-icon" aria-hidden="true">
            <AlertIcon icon="shield" />
          </span>
          {loading ? 'Saving...' : editingId ? 'Save Rule' : 'Create Rule'}
        </button>
      </div>
    </form>
  );
}

function RuleCard({
  alert,
  device,
  handleDelete,
  handleEdit,
  handleToggle,
  selected,
  setSelectedRuleId,
}: {
  alert: Alert;
  device?: Device;
  handleDelete: (id: string) => void;
  handleEdit: (alert: Alert) => void;
  handleToggle: (alert: Alert) => void;
  selected: boolean;
  setSelectedRuleId: (id: string) => void;
}) {
  const icon = getMetricIcon(alert.metric);

  return (
    <article className={`sd-alert-rule-card ${selected ? 'is-selected' : ''} ${alert.enabled ? 'is-active' : 'is-paused'}`}>
      <button className="sd-alert-rule-main" onClick={() => setSelectedRuleId(alert.id)} type="button">
        <span className={`sd-alert-rule-icon is-${getMetricTone(alert.metric)}`} aria-hidden="true">
          <AlertIcon icon={icon} />
        </span>
        <span className="sd-alert-rule-copy">
          <strong>{alert.name}</strong>
          <small>{device?.name || alert.device_name || 'Unknown device'}</small>
        </span>
        <RuleStatus alert={alert} />
      </button>

      <div className="sd-alert-rule-facts">
        <RuleMiniFact label="Condition" value={`${alert.metric} ${alert.operator} ${String(alert.threshold)}`} mono />
        <RuleMiniFact label="Cooldown" value={`${alert.cooldown_minutes} min`} />
        <RuleMiniFact label="Channels" value={formatChannels(alert)} />
      </div>

      <div className="sd-alert-card-actions">
        <button onClick={() => handleEdit(alert)} type="button">
          <AlertIcon icon="edit" />
          Edit
        </button>
        <button onClick={() => handleToggle(alert)} type="button">
          <AlertIcon icon={alert.enabled ? 'pause' : 'play'} />
          {alert.enabled ? 'Pause' : 'Activate'}
        </button>
        <button className="is-danger" onClick={() => handleDelete(alert.id)} type="button">
          <AlertIcon icon="trash" />
          Delete
        </button>
      </div>
    </article>
  );
}

function RuleInspector({
  device,
  formatTime,
  onDelete,
  onEdit,
  onToggle,
  rule,
}: {
  device?: Device;
  formatTime: (value: string) => string;
  onDelete: (id: string) => void;
  onEdit: (alert: Alert) => void;
  onToggle: (alert: Alert) => void;
  rule: Alert | null;
}) {
  if (!rule) {
    return (
      <div className="sd-alert-selected-empty">
        <span className="sd-alert-empty-icon" aria-hidden="true">
          <AlertIcon icon="bell" />
        </span>
        <strong>Select a rule</strong>
        <p>Choose an alert from coverage to inspect its condition and notification setup.</p>
      </div>
    );
  }

  return (
    <div className="sd-alert-selected-content">
      <div className="sd-alert-selected-head">
        <div>
          <span className="sd-alert-section-kicker">
            <i />
            Selected Rule
          </span>
          <h2>{rule.name}</h2>
          <p>{device?.name || rule.device_name || 'Unknown device'}</p>
        </div>
        <RuleStatus alert={rule} />
      </div>

      <div className="sd-alert-selected-actions">
        <button onClick={() => onEdit(rule)} type="button">
          <AlertIcon icon="edit" />
          Edit
        </button>
        <button onClick={() => onToggle(rule)} type="button">
          <AlertIcon icon={rule.enabled ? 'pause' : 'play'} />
          {rule.enabled ? 'Pause' : 'Activate'}
        </button>
        <button className="is-danger" onClick={() => onDelete(rule.id)} type="button">
          <AlertIcon icon="trash" />
          Delete
        </button>
      </div>

      <div className="sd-alert-condition-card">
        <span>If</span>
        <strong>{formatMetricLabel(rule.metric)}</strong>
        <b>{rule.operator} {String(rule.threshold)}</b>
      </div>

      <div className="sd-inspector-facts sd-alert-inspector-facts">
        <FactRow label="Device" value={device?.name || rule.device_name || 'Unknown'} />
        <FactRow label="Topic" value={device?.topic || 'Unknown'} mono />
        <FactRow label="Cooldown" value={`${rule.cooldown_minutes} min`} />
        <FactRow label="Last trigger" value={rule.last_triggered ? formatTime(rule.last_triggered) : 'Never'} />
        <FactRow label="Channels" value={formatChannels(rule)} />
      </div>
    </div>
  );
}

function AlertTimeline({
  expanded,
  formatTime,
  logs,
  onToggleExpanded,
}: {
  expanded: boolean;
  formatTime: (value: string) => string;
  logs: AlertLog[];
  onToggleExpanded: () => void;
}) {
  const [page, setPage] = useState(0);
  const pageSize = 50;
  const visibleLogs = expanded ? logs.slice(page * pageSize, page * pageSize + pageSize) : logs.slice(0, 5);
  const totalPages = Math.max(1, Math.ceil(logs.length / pageSize));
  const pageStart = page * pageSize;
  const shouldPaginate = expanded && logs.length > pageSize;

  useEffect(() => {
    if (!expanded) setPage(0);
  }, [expanded]);

  useEffect(() => {
    setPage((current) => Math.min(current, totalPages - 1));
  }, [totalPages]);

  return (
    <section className="sd-alert-timeline-panel">
      <div className="sd-alert-subpanel-head">
        <div>
          <span>Trigger Timeline</span>
          <strong>{expanded ? 'Latest 50 events' : 'Last activity'}</strong>
        </div>
        {logs.length > 5 && (
          <button onClick={onToggleExpanded} type="button">
            {expanded ? 'Show latest' : 'Full history'}
          </button>
        )}
      </div>

      <div className="sd-alert-timeline-list">
        {visibleLogs.length === 0 ? (
          <AlertEmptyState label="No alert triggers yet." />
        ) : (
          visibleLogs.map((log) => (
            <article className={`sd-alert-timeline-row is-${getLogTone(log)}`} key={log.id}>
              <span className="sd-alert-timeline-dot" aria-hidden="true" />
              <div className="sd-alert-timeline-copy">
                <time className="sd-mono">{formatTime(log.triggered_at)}</time>
                <strong>{log.alert_name || 'Unknown alert'}</strong>
                <span>{log.device_name || 'Unknown device'}</span>
                <code>{log.metric} {log.operator} {String(log.threshold)}</code>
              </div>
              <strong className="sd-alert-actual">{formatAlertValue(log.actual_value)}</strong>
            </article>
          ))
        )}
      </div>
      {shouldPaginate && (
        <div className="sd-alert-timeline-footer">
          <span>
            Showing {pageStart + 1}-{Math.min(pageStart + pageSize, logs.length)} of {logs.length}
          </span>
          <div>
            <button disabled={page === 0} onClick={() => setPage((current) => Math.max(0, current - 1))} type="button">
              Previous
            </button>
            <button disabled={page >= totalPages - 1} onClick={() => setPage((current) => Math.min(totalPages - 1, current + 1))} type="button">
              Next
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function NotificationChannels({
  setTestEmail,
  setTestTelegram,
  testEmail,
  testEmailChannel,
  testTelegram,
  testTelegramChannel,
}: {
  setTestEmail: (value: string) => void;
  setTestTelegram: (value: string) => void;
  testEmail: string;
  testEmailChannel: () => void;
  testTelegram: string;
  testTelegramChannel: () => void;
}) {
  return (
    <section className="sd-alert-channel-panel">
      <div className="sd-alert-subpanel-head">
        <div>
          <span>Notification Channels</span>
          <strong>Test delivery</strong>
        </div>
      </div>

      <div className="sd-alert-channel-list">
        <article className="sd-alert-channel-card">
          <span className="sd-alert-channel-icon" aria-hidden="true">
            <AlertIcon icon="mail" />
          </span>
          <label className="sd-field">
            <span>Email</span>
            <input
              type="email"
              placeholder="engineer@company.com"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
            />
          </label>
          <button onClick={testEmailChannel} type="button">Test</button>
        </article>

        <article className="sd-alert-channel-card">
          <span className="sd-alert-channel-icon is-violet" aria-hidden="true">
            <AlertIcon icon="telegram" />
          </span>
          <label className="sd-field">
            <span>Telegram</span>
            <input
              placeholder="123456789"
              value={testTelegram}
              onChange={(e) => setTestTelegram(e.target.value)}
            />
          </label>
          <button onClick={testTelegramChannel} type="button">Test</button>
        </article>
      </div>
    </section>
  );
}

function StepHeading({
  copy,
  icon,
  number,
  title,
}: {
  copy: string;
  icon: AlertIconName;
  number: string;
  title: string;
}) {
  return (
    <div className="sd-alert-form-step-head">
      <span className="sd-alert-step-number">{number}</span>
      <span className="sd-alert-flow-icon" aria-hidden="true">
        <AlertIcon icon={icon} />
      </span>
      <div>
        <strong>{title}</strong>
        <small>{copy}</small>
      </div>
    </div>
  );
}

function AlertStat({
  detail,
  label,
  tone,
  value,
}: {
  detail: string;
  label: string;
  tone: AlertTone;
  value: number | string;
}) {
  return (
    <article className={`sd-alert-stat is-${tone}`}>
      <span>{label}</span>
      <strong>{value}</strong>
      <p>{detail}</p>
    </article>
  );
}

function RuleStatus({ alert }: { alert: Alert }) {
  return (
    <span className={`sd-rule-status ${alert.enabled ? 'is-active' : 'is-paused'}`}>
      <i />
      {alert.enabled ? 'Active' : 'Paused'}
    </span>
  );
}

function RuleMiniFact({ label, mono, value }: { label: string; mono?: boolean; value: string }) {
  return (
    <div className="sd-alert-mini-fact">
      <span>{label}</span>
      <strong className={mono ? 'sd-mono' : undefined}>{value}</strong>
    </div>
  );
}

function Field({ children, hint, label }: { children: React.ReactNode; hint?: string; label: string }) {
  return (
    <label className="sd-field">
      <span>{label}</span>
      {children}
      {hint && <small>{hint}</small>}
    </label>
  );
}

function FactRow({ label, mono, value }: { label: string; mono?: boolean; value: string }) {
  return (
    <div className="sd-fact-row">
      <span>{label}</span>
      <strong className={mono ? 'sd-mono' : undefined}>{value}</strong>
    </div>
  );
}

function AlertEmptyState({ label }: { label: string }) {
  return (
    <div className="sd-alert-empty">
      <span className="sd-alert-empty-icon" aria-hidden="true">
        <AlertIcon icon="bell" />
      </span>
      <strong>{label}</strong>
      <span>Use a threshold rule to start monitoring device conditions.</span>
    </div>
  );
}

function getWorkflowSteps(rule: Alert | null, device?: Device, latestLog?: AlertLog | null) {
  return [
    {
      number: '1',
      title: 'Choose Device',
      icon: 'device' as AlertIconName,
      tone: 'blue' as AlertTone,
      primary: device?.name || rule?.device_name || 'Select an asset',
      secondary: rule ? 'Selected' : 'Ready',
    },
    {
      number: '2',
      title: 'Pick Metric',
      icon: getMetricIcon(rule?.metric || 'temperature'),
      tone: 'green' as AlertTone,
      primary: rule ? formatMetricLabel(rule.metric) : 'Metric field',
      secondary: latestLog ? `Last ${formatAlertValue(latestLog.actual_value)}` : 'Mapped field',
    },
    {
      number: '3',
      title: 'Set Condition',
      icon: 'condition' as AlertIconName,
      tone: 'amber' as AlertTone,
      primary: rule ? `${rule.operator} ${String(rule.threshold)}` : 'Threshold rule',
      secondary: rule ? 'Configured' : 'Waiting',
    },
    {
      number: '4',
      title: 'Notify Team',
      icon: 'mail' as AlertIconName,
      tone: 'violet' as AlertTone,
      primary: rule ? formatChannels(rule) : 'Email or Telegram',
      secondary: rule && (rule.notify_email || rule.notify_telegram) ? 'Configured' : 'Optional',
    },
  ];
}

function getMetricIcon(metric: string): AlertIconName {
  const normalized = metric.toLowerCase();
  if (normalized.includes('temp')) return 'thermometer';
  if (normalized.includes('pressure')) return 'gauge';
  if (normalized.includes('humidity') || normalized.includes('flow')) return 'droplet';
  if (normalized.includes('door') || normalized.includes('state')) return 'shield';
  return 'bell';
}

function getMetricTone(metric: string): AlertTone {
  const normalized = metric.toLowerCase();
  if (normalized.includes('temp')) return 'danger';
  if (normalized.includes('pressure')) return 'amber';
  if (normalized.includes('humidity') || normalized.includes('flow')) return 'blue';
  if (normalized.includes('door') || normalized.includes('state')) return 'green';
  return 'violet';
}

function getLogTone(log: AlertLog): AlertTone {
  const actual = Number(log.actual_value);
  const threshold = Number(log.threshold);
  if (Number.isFinite(actual) && Number.isFinite(threshold)) {
    const spread = Math.abs(actual - threshold);
    const ratio = threshold === 0 ? spread : spread / Math.abs(threshold);
    return ratio >= 0.08 ? 'danger' : 'amber';
  }
  return 'amber';
}

function formatMetricLabel(metric: string): string {
  return metric.replace(/_/g, ' ');
}

function formatAlertValue(value: string | number | undefined | null): string {
  if (value === undefined || value === null) return '--';
  if (typeof value === 'number') return value.toFixed(1);
  const num = parseFloat(value);
  if (!Number.isNaN(num) && Number.isFinite(num) && value.trim() !== '') return num.toFixed(1);
  return value;
}

function formatChannels(alert: Alert) {
  const channels = [
    alert.notify_email ? 'Email' : null,
    alert.notify_telegram ? 'Telegram' : null,
  ].filter(Boolean);
  return channels.length > 0 ? channels.join(' / ') : 'None';
}

function AlertIcon({ icon }: { icon: AlertIconName }) {
  const common = {
    fill: 'none',
    stroke: 'currentColor',
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    strokeWidth: 2,
  };

  return (
    <svg aria-hidden="true" focusable="false" viewBox="0 0 24 24">
      {icon === 'bell' && (
        <>
          <path {...common} d="M18 8a6 6 0 0 0-12 0c0 7-3 8-3 8h18s-3-1-3-8" />
          <path {...common} d="M13.7 21a2 2 0 0 1-3.4 0" />
        </>
      )}
      {icon === 'device' && (
        <>
          <rect {...common} x="4" y="5" width="16" height="14" rx="2" />
          <path {...common} d="M8 9h3" />
          <path {...common} d="M8 13h8" />
          <path {...common} d="M15 9h1" />
        </>
      )}
      {icon === 'gauge' && (
        <>
          <path {...common} d="M4 15a8 8 0 1 1 16 0" />
          <path {...common} d="m12 15 4-5" />
          <path {...common} d="M8 17h8" />
        </>
      )}
      {icon === 'thermometer' && (
        <>
          <path {...common} d="M14 14.8V5a2 2 0 0 0-4 0v9.8a4 4 0 1 0 4 0Z" />
          <path {...common} d="M12 8v7" />
        </>
      )}
      {icon === 'condition' && (
        <>
          <path {...common} d="M4 8h6" />
          <path {...common} d="M4 16h6" />
          <path {...common} d="m14 7 3 3 3-3" />
          <path {...common} d="m14 17 3-3 3 3" />
        </>
      )}
      {icon === 'mail' && (
        <>
          <rect {...common} x="3" y="5" width="18" height="14" rx="2" />
          <path {...common} d="m3 7 9 6 9-6" />
        </>
      )}
      {icon === 'telegram' && (
        <>
          <path {...common} d="m21 3-7 18-4-8-7-4 18-6Z" />
          <path {...common} d="m10 13 4-4" />
        </>
      )}
      {icon === 'plus' && (
        <>
          <path {...common} d="M12 5v14" />
          <path {...common} d="M5 12h14" />
        </>
      )}
      {icon === 'refresh' && (
        <>
          <path {...common} d="M20 11a8 8 0 0 0-14.4-4.8L4 8" />
          <path {...common} d="M4 4v4h4" />
          <path {...common} d="M4 13a8 8 0 0 0 14.4 4.8L20 16" />
          <path {...common} d="M16 16h4v4" />
        </>
      )}
      {icon === 'edit' && (
        <>
          <path {...common} d="M12 20h9" />
          <path {...common} d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5Z" />
        </>
      )}
      {icon === 'pause' && (
        <>
          <path {...common} d="M8 5v14" />
          <path {...common} d="M16 5v14" />
        </>
      )}
      {icon === 'play' && <path {...common} d="m8 5 11 7-11 7V5Z" />}
      {icon === 'trash' && (
        <>
          <path {...common} d="M3 6h18" />
          <path {...common} d="M8 6V4h8v2" />
          <path {...common} d="M19 6l-1 14H6L5 6" />
          <path {...common} d="M10 11v5" />
          <path {...common} d="M14 11v5" />
        </>
      )}
      {icon === 'shield' && (
        <>
          <path {...common} d="M12 3 5 6v6c0 4.5 3 7.5 7 9 4-1.5 7-4.5 7-9V6l-7-3Z" />
          <path {...common} d="m9 12 2 2 4-5" />
        </>
      )}
      {icon === 'timeline' && (
        <>
          <path {...common} d="M12 5v14" />
          <circle {...common} cx="12" cy="6" r="2" />
          <circle {...common} cx="12" cy="12" r="2" />
          <circle {...common} cx="12" cy="18" r="2" />
        </>
      )}
      {icon === 'droplet' && <path {...common} d="M12 3s6 6 6 11a6 6 0 0 1-12 0c0-5 6-11 6-11Z" />}
      {icon === 'x' && (
        <>
          <path {...common} d="M6 6l12 12" />
          <path {...common} d="M18 6 6 18" />
        </>
      )}
    </svg>
  );
}
